import React from 'react';
import PropTypes from 'prop-types';
import { CircularProgressbar } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';

const ProfileTabs = ({
  user,
  activeTab,
  setActiveTab,
  onJoinOrganization,
  onRevokeJoining,
  permissions,
  isFormComplete,
  onEdit,
  progress
}) => {
  const hasPrivilege = (privilege) => permissions.includes(privilege);
  const isRejected = hasPrivilege('VIEW:REJECTED');
  const isWaiting = hasPrivilege('VIEW:WAITING_MEMBER');
  const hasRevisionComment = !!user?.revisionComment;

  const renderProgress = (tab) => (
      <div className="progress-section">
        <div className="progress-wrapper">
          <CircularProgressbar
              value={progress[tab]}
              text={`${progress[tab]}%`}
              styles={{
                path: { stroke: '#3498db' },
                text: { fill: '#2c3e50', fontSize: '24px' },
                trail: { stroke: '#f0f0f0' }
              }}
          />
        </div>
        <div className="progress-label">
          Заполнено полей: {progress[tab]}%
        </div>
      </div>
  );

  const renderOrganizationTab = () => (
      <div className="tab-content">
        <div className="organization-info">
          <div className="status-section">
            {hasPrivilege('VIEW:MEMBER') ? (
                <div className="status-message success">
                  ✅ Вы являетесь членом нашей организации
                </div>
            ) : isWaiting ? (
                <>
                  <div className="status-message warning">
                    ⏳ Ваша заявка находится на рассмотрении
                  </div>
                  <button className="revoke-button" onClick={onRevokeJoining}>
                    Отозвать заявку
                  </button>
                </>
            ) : isRejected ? (
                <div className="status-message error">
                  ❌ Ваша заявка была отклонена
                  {hasRevisionComment && (
                      <div className="revision-comment">
                        <h4>Комментарий модератора:</h4>
                        <p>{user.revisionComment}</p>
                      </div>
                  )}
                </div>
            ) : (
                <div className="join-section">
                  {hasRevisionComment && (
                      <div className="revision-notice">
                        <h4>Требуется доработка:</h4>
                        <p>{user.revisionComment}</p>
                      </div>
                  )}
                  <button
                      className={`join-button ${!isFormComplete ? 'disabled' : ''}`}
                      onClick={onJoinOrganization}
                      disabled={!isFormComplete}
                  >
                    {hasRevisionComment ? 'Подать заявку повторно' : 'Подать заявку на вступление'}
                  </button>
                  {!isFormComplete && (
                      <div className="form-notice">
                        Заполните все обязательные поля в профиле
                      </div>
                  )}
                </div>
            )}
          </div>
        </div>
      </div>
  );


  const renderMembershipTab = () => (
      <div className="tab-content">
        {renderProgress('membership')}
        <div className="membership-info">
          <div className="info-grid">
            <InfoItem
                label="Членство в других организациях"
                value={user?.memberships || 'Не состоит'}
            />
            <InfoItem label="Мотивация" value={user?.motivation} />
            <InfoItem label="Родитель/опекун" value={user?.parentFullName} />
            <InfoItem label="Контакт родителя" value={user?.parentPhoneNumber} />
            <InfoItem label="Email родителя" value={user?.parentEmail} />
          </div>
        </div>
      </div>
  );

  const renderTabContent = () => {
    switch(activeTab) {
      case 'personal':
        return (
            <div className="tab-content">
              {renderProgress('personal')}
              <div className="info-grid">
                <InfoItem label="Имя" value={user?.firstName} />
                <InfoItem label="Фамилия" value={user?.lastName} />
                <InfoItem label="Отчество" value={user?.patronymic} />
                <InfoItem label="Пол" value={user?.gender} />
                <InfoItem label="Дата рождения" value={user?.birthDate} />
                <InfoItem label="Место рождения" value={user?.birthPlace} />
                <InfoItem label="Национальность" value={user?.nationality} />
              </div>
            </div>
        );

      case 'contacts':
        return (
            <div className="tab-content">
              {renderProgress('contacts')}
              <div className="info-grid">
                <InfoItem label="Email" value={user?.email} />
                <InfoItem label="Телефон" value={user?.phoneNumber} />
                <InfoItem label="Адрес" value={user?.address} />
                <InfoItem label="Соцсети" value={user?.socialMedia} />
                <InfoItem label="Адрес регистрации" value={user?.registrationAddress} />
              </div>
            </div>
        );

      case 'documents':
        return (
            <div className="tab-content">
              {renderProgress('documents')}
              <div className="document-section">
                <h3>Паспортные данные</h3>
                <div className="info-grid">
                  <InfoItem label="Серия" value={user?.passportSeries} />
                  <InfoItem label="Номер" value={user?.passportNumber} />
                  <InfoItem label="Кем выдан" value={user?.passportIssuedBy} />
                  <InfoItem
                      label="Дата выдачи"
                      value={user?.passportIssueDate?.toString()}
                  />
                </div>

                <h3>Образование</h3>
                <div className="info-grid">
                  <InfoItem label="Уровень образования" value={user?.education} />
                </div>
              </div>
            </div>
        );

      case 'organization':
        return renderOrganizationTab();

      case 'membership':
        return renderMembershipTab();

      default:
        return null;
    }
  };

  return (
      <div className="tabs-container">
        <div className="tabs-header">
          <nav className="tabs-nav">
            {['personal', 'contacts', 'documents', 'membership', 'organization'].map((tab) => (
                <button
                    key={tab}
                    className={`tab-button ${activeTab === tab ? 'active' : ''}`}
                    onClick={() => setActiveTab(tab)}
                >
                  {getTabLabel(tab)}
                </button>
            ))}
          </nav>
          <button
              className="edit-profile-button"
              onClick={onEdit}
          >
            Редактировать профиль
          </button>
        </div>

        {renderTabContent()}
      </div>
  );
};

const InfoItem = ({ label, value }) => (
    <div className="info-item">
      <span className="info-label">{label}:</span>
      <span className="info-value">{value || 'Не указано'}</span>
    </div>
);

const getTabLabel = (tab) => {
  const labels = {
    personal: 'Личные данные',
    contacts: 'Контакты',
    documents: 'Документы',
    organization: 'Моя организация',
    membership: 'Прочее'
  };
  return labels[tab];
};

ProfileTabs.propTypes = {
  user: PropTypes.object.isRequired,
  activeTab: PropTypes.string.isRequired,
  setActiveTab: PropTypes.func.isRequired,
  onJoinOrganization: PropTypes.func.isRequired,
  onRevokeJoining: PropTypes.func.isRequired,
  permissions: PropTypes.array.isRequired,
  isFormComplete: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
  progress: PropTypes.object.isRequired
};

export default ProfileTabs;